// @ts-nocheck
import findNextDate from '@edu-match/utils/findNextDate';
import Autolinker from 'autolinker';
import cn from 'classnames';
import { useEffect, useState, useMemo } from 'react';
import { HiOutlineClock, HiLocationMarker, HiOutlineClipboardList } from 'react-icons/hi';
import { useSelector, useDispatch } from 'react-redux';

import useElementEvents from '@core/hooks/cohesion/useElementEvents';
import useDualCtaTest from '@core/hooks/useDualCtaTest';
import useSchoolConfig from '@core/hooks/useSchoolConfig';
import useStartYourApplicationEditableAsset from '@core/hooks/useStartYourApplicationEditableAsset';
import {
  selectHeclidMap,
  selectProductCorrelationIdMap,
  selectViewCorrelationIdMap,
} from '@core/reducers/eventingSlice';
import { selectInput } from '@core/reducers/inputsSlice';
import { selectLeadDelivery } from '@core/reducers/matchesSlice';
import { mobiusGetApplicationCta } from '@core/services/mobius';

import Dots from '../Loading/Dots';
import styles from './ExitStrategyV2.module.css';
import { applicationCtaBtn, continueBrowsingBtn, slideOutClose, slideOutDismiss } from './eventPayloads';
import type { ExitStrategyComponent, SchoolConfigData } from './type';

const ExitStrategy: ExitStrategyComponent = ({ setSlideOutActions }) => {
  // Store
  const firstName = useSelector((state) => selectInput(state, 'firstName'));
  const { currentPrograms } = useSelector(selectLeadDelivery);
  const heclidMap = useSelector(selectHeclidMap);
  const productCorrelationIdMap = useSelector(selectProductCorrelationIdMap);
  const viewCorrelationIdMap = useSelector(selectViewCorrelationIdMap);
  const { program, school } = currentPrograms[0];

  // Purdue Book Me Test / Dual CTA Test
  const { DualCtaTestComponent, shouldApplyWrapperStyles } = useDualCtaTest(school.id.toString());
  const { dualCtaEnabled, isTestEnabled } = useStartYourApplicationEditableAsset(school.id?.toString() || '');

  // Action Dispatcher
  const dispatch = useDispatch();

  const { data, loading }: SchoolConfigData = useSchoolConfig(school.id);

  const programDates = useMemo(
    () => (data?.formDates?.undergrad?.length ? findNextDate(data?.formDates?.undergrad) : {}),
    [data]
  );

  // Local state
  const [applicationCtaData, setApplicationCtaData] = useState<{ label: string; url: string } | null>(null);

  const introText =
    data?.confirmation?.meta.shortIntro ||
    data?.confirmation?.meta.text ||
    'An Admissions Advisor will be in touch soon.';

  const autolinkedIntroText = Autolinker.link(introText);

  const showViewMoreBtn = autolinkedIntroText?.length > 110;

  const { elementViewed, elementClicked } = useElementEvents({
    correlationId: viewCorrelationIdMap[program.id],
  });

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (setSlideOutActions) {
      setSlideOutActions({
        close: () => elementClicked(slideOutClose),
        dismiss: () => elementClicked(slideOutDismiss),
      });

      // This makes sure to set the callbacks back to undefined
      //  after the child component is unmounted
      return () => {
        setSlideOutActions(undefined);
      };
    }
  }, []);

  useEffect(() => {
    elementViewed(continueBrowsingBtn);

    const getApplicationCtaData = async () => {
      const response = await mobiusGetApplicationCta({
        programId: `${program.id}`,
        heclid: heclidMap[program.id],
        correlationId: productCorrelationIdMap[program.id],
      });
      setApplicationCtaData({ label: response?.label, url: response?.url });
    };

    getApplicationCtaData();
  }, []);

  useEffect(() => {
    if (applicationCtaData?.url) {
      elementViewed(applicationCtaBtn);
    }
  }, [applicationCtaData]);

  const handleApplication = (e, url: string) => {
    e.preventDefault();
    elementClicked({ ...applicationCtaBtn, outboundUrl: applicationCtaData?.url });
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const nextStepSchools = ['purdue-university-global', 'southern-new-hampshire-university'];

  return (
    <>
      {loading ? (
        <section className={styles.loadingContainer}>
          <p className={styles.loadingText}>Submitting your info</p>
          <Dots />
        </section>
      ) : (
        <section className={styles.exitStrategyContainer}>
          <div className={styles.scrollContainer}>
            <div className={styles.header}>
              <img
                src={
                  school?.slug
                    ? `https://simple-storage-server.highereducation.com/${school.slug}.png?w=130&h=80`
                    : undefined
                }
                alt={school?.name}
              />
              <div className={styles.schoolInfoContainer}>
                <h4 className={styles.title}>{school?.name}</h4>
                {school?.city && school?.state && (
                  <div className={styles.location}>
                    <span className={styles.locationIcon}>
                      <HiLocationMarker size={16} />
                    </span>{' '}
                    {school.city}, {school.state}
                  </div>
                )}
              </div>
            </div>
            <div className={styles.bodyContainer}>
              <div className={styles.congrats}>
                <h2>Congratulations{firstName ? `, ${firstName}` : ''}!</h2>
                <p>Your information was submitted successfully.</p>
              </div>

              <p className={styles.noDate}>
                <span className={styles.clockIcon}>
                  <HiOutlineClock size={20} />
                </span>
                Now accepting new students.
              </p>
              <div className={styles.applyOnline}>
                <h3>
                  <span className={styles.clipboardIcon}>
                    <HiOutlineClipboardList size={20} />
                  </span>
                  Apply Online Today
                </h3>
                <p>
                  Online applications are fast, convenient, and let you track your acceptance status. Start yours today!
                </p>
              </div>

              {/* We only show this section if Mobius returns us a valid URL */}
              {applicationCtaData?.url && (
                <section className={cn(styles.applicationContainer, isTestEnabled && dualCtaEnabled && styles.dualCta)}>
                  {isTestEnabled && dualCtaEnabled && (
                    <DualCtaTestComponent schoolId={school.id?.toString() || ''} className={styles.ctaButton} />
                  )}
                  <button type="button" onClick={(e) => handleApplication(e, applicationCtaData?.url)}>
                    {applicationCtaData?.label ?? 'Start Your Application'}
                  </button>
                </section>
              )}

              <section className={styles.exitStrategyContentContainer}>
                <div>
                  <h2>What to expect from your application:</h2>
                  <ul>
                    <li>Most applications don&apos;t take long to complete.</li>
                    <li>
                      Be prepared to highlight your strengths, achievements, and individuality to show the school who
                      you are.
                    </li>
                    <li>
                      Depending on the school and degree level, you may be asked to provide your GPA and test scores.
                    </li>
                    <li>
                      After submitting your application, track its status online. The school should have a decision
                      soon.
                    </li>
                  </ul>
                </div>
                <div>
                  <h2 className={styles.secondTitle}>Preparing for a chat with your advisor:</h2>
                  <ul>
                    <li>
                      Generally, you can expect to be called immediately or within 2 business days, depending on the
                      availability of the school&apos;s counselors. If you miss your call, {school?.name} will try
                      again.
                    </li>
                    <li>
                      Calls average 20-30 minutes, and you&apos;ll typically receive a follow-up email afterwards.
                    </li>
                    <li>Be prepared to talk about your work and school history.</li>
                    <li>Write down a few questions before the call</li>
                  </ul>
                </div>
                <div>
                  <h2 className={styles.secondTitle}>Here&apos;s what other students are asking:</h2>
                  <ul>
                    <li>What type of courses will I be taking for my degree?</li>
                    <li>What scholarships and financial aid are available?</li>
                    <li>How can I estimate my tuition cost, including books and fees?</li>
                  </ul>
                </div>
              </section>
            </div>
          </div>
        </section>
      )}
    </>
  );
};

export { ExitStrategy };
